/**
 * Repetition law — the technical core of this whole project.
 *
 * Target rule set: WXF / AXF (algorithmically decidable). The Chinese national
 * (CXA) definition of 捉 rests on material-gain and mating-intent judgments that
 * are not decidable without search, so it is out of scope as a normative target.
 *
 * Violation lattice (higher loses; equal draws):
 *
 *     PerpetualIdle (0)  <  PerpetualChase (1)  <  PerpetualCheck (2)
 *
 * When a position repeats, compare the two sides' violation levels over the
 * repeated cycle: the side with the strictly higher level loses; equal levels
 * draw. This is the shared shape of Pikafish's `rule_judge` and the Tan &
 * Watkinson (arXiv 2412.17334) formulation.
 *
 * This module currently implements the parts that are unambiguous and testable:
 *   - cycle / N-fold repetition detection over a move history,
 *   - perpetual-CHECK adjudication (uncontroversial; everyone agrees on it),
 *     wired through Game.repetitionOutcome() and used by the live game server.
 *
 * Until 2026-09-25 the check half was present but DEAD: adjudicatePerpetualCheck
 * had no callers and nothing computed its two booleans, so a perpetual check was
 * scored as a draw in real games while the site claimed otherwise. If you are
 * adding to this module, add the caller in the same change.
 *
 * Perpetual-CHASE (長捉) detection is now implemented from the same published
 * specification, and is NOT validated against the WXF 110-diagram casebook,
 * because no machine-readable copy of it exists. Two flags at the bottom of this
 * file say exactly that, and the site copy is written from both. Do not collapse
 * them back into one: conflating "implemented" with "verified" is how this
 * project came to publish a verification claim it could not support.
 *
 * The chase algorithm to implement (reimplemented from the published Pikafish
 * spec at pikafish.com/rule.html, NOT ported from GPL source, plus the paper's
 * diagram-40/41 fix):
 *
 *   For each ply in the repeated cycle, compute the set of enemy pieces this
 *   side "chases" — identified by a STABLE per-piece id, not by square, so a
 *   piece that moves mid-cycle is still tracked. An attack counts as a chase
 *   unless:
 *     (1) the victim is protected — i.e. the attacker would itself be capturable
 *         after capturing — EXCEPT the value overrides where the exchange is
 *         always good: 馬/炮 attacking 車, and 仕/相 attacking 馬/炮/車;
 *     (2) the attacker is a king or pawn;
 *     (3) the victim is a pawn that has not crossed the river;
 *     (4) it is a symmetric same-type mutual attack (rook-vs-rook standoff).
 *   Protection is evaluated from the PRE-move position (the diagram-40/41 fix).
 *   AND-narrow the chased-piece set across every ply of the cycle; a non-empty
 *   result means one specific piece was chased on every move -> perpetual chase.
 *   Any CHECK appearing anywhere in the cycle voids chase adjudication entirely
 *   (一將一捉 is permitted): treat the cycle as check-based instead.
 */

import { inCheck, isSquareAttacked } from "./attacks.js";
import { legalMoves } from "./movegen.js";
import { Position } from "./position.js";
import { toFen } from "./fen.js";
import { Color, opponent, pawnHasCrossed, PieceType, type Move, type Square } from "./types.js";
import { GameResult } from "./status.js";

export enum ViolationLevel {
  Idle = 0,
  Chase = 1,
  Check = 2,
}

/**
 * A position key for repetition comparison: the placement + side to move.
 * Move counters are intentionally excluded — repetition is about the board, not
 * the clock.
 */
export function repetitionKey(pos: Position): string {
  const fen = toFen(pos);
  // Keep placement and side-to-move; drop the counters.
  const parts = fen.split(" ");
  return `${parts[0]} ${parts[1]}`;
}

/** Count how many times `key` appears in a history of position keys. */
export function repetitionCount(history: readonly string[], key: string): number {
  let n = 0;
  for (const k of history) if (k === key) n += 1;
  return n;
}

/**
 * Resolve a perpetual-CHECK situation over a repeated cycle. `checkedBySide`
 * indicates, for each color, whether every one of that color's moves in the
 * cycle delivered check. The perpetual checker loses; if both perpetually
 * check, it is a draw.
 *
 * This is the fully-settled half of the repetition law. The chase half is
 * pending (see module header).
 */
export function adjudicatePerpetualCheck(
  redAlwaysChecks: boolean,
  blackAlwaysChecks: boolean,
): GameResult {
  if (redAlwaysChecks && blackAlwaysChecks) return GameResult.Draw;
  if (redAlwaysChecks) return GameResult.BlackWins; // Red is the sole perpetual checker -> Red loses
  if (blackAlwaysChecks) return GameResult.RedWins;
  return GameResult.Draw;
}

/**
 * One ply of a repeated cycle: who moved, and whether that move gave check.
 *
 * This is the missing link the law needs and a position cannot supply. Whether a
 * repetition is a perpetual check depends on what happened on the way round the
 * cycle, so the caller (Game) records it per move and hands the cycle here.
 */
export interface CyclePly {
  readonly mover: Color;
  readonly gaveCheck: boolean;
  /**
   * Stable ids of the enemy pieces this move leaves under threat of capture,
   * filtered by the chase exceptions.
   *
   * Ids, not squares, and that is the whole difficulty: the law asks whether the
   * SAME piece was chased on every move of the cycle, and a chased piece is
   * usually running, so its square changes every ply. The caller (Game) keeps a
   * square-to-id map across moves.
   */
  readonly chasedIds: readonly number[];
}

/**
 * Which sides checked on EVERY one of their own moves in the cycle.
 *
 * "Every one of its own moves" is the test the law uses, not "some move gave
 * check": a side that checks once and then does something else is not
 * perpetually checking, and one that alternates check with a quiet move has
 * varied. A side with no moves in the cycle cannot be the perpetual checker, so
 * it reports false rather than vacuously true.
 */
export function perpetualCheckers(cycle: readonly CyclePly[]): { red: boolean; black: boolean } {
  let redMoves = 0;
  let redChecks = 0;
  let blackMoves = 0;
  let blackChecks = 0;
  for (const ply of cycle) {
    if (ply.mover === Color.Red) {
      redMoves += 1;
      if (ply.gaveCheck) redChecks += 1;
    } else {
      blackMoves += 1;
      if (ply.gaveCheck) blackChecks += 1;
    }
  }
  return {
    red: redMoves > 0 && redChecks === redMoves,
    black: blackMoves > 0 && blackChecks === blackMoves,
  };
}

/* ------------------------------------------------------------------- chase

   Reimplemented from the published Pikafish specification (pikafish.com/rule.html)
   plus the diagram-40/41 fix described in Tan & Watkinson (arXiv 2412.17334).
   NOT ported from GPL source.

   Every exception below makes a threat NOT a chase, so a missing one produces a
   FALSE POSITIVE -- someone loses a game they should have drawn. That asymmetry
   decides the doubtful calls throughout: when unsure, do not call it a chase.
*/

/**
 * Exchanges good enough that a defended victim is still worth chasing.
 *
 * Normally a protected piece is not being chased, because the threat is not a
 * threat -- capturing loses more than it wins. These are the pairings where it
 * wins anyway, so protection is no excuse.
 */
function exchangeAlwaysGood(attacker: PieceType, victim: PieceType): boolean {
  // 馬 or 炮 taking 車: a horse or cannon for a chariot is always worth it.
  if (attacker === PieceType.Horse || attacker === PieceType.Cannon) {
    return victim === PieceType.Rook;
  }
  // 仕 or 相 taking 馬, 炮 or 車: likewise, and more so.
  if (attacker === PieceType.Advisor || attacker === PieceType.Elephant) {
    return victim === PieceType.Horse || victim === PieceType.Cannon || victim === PieceType.Rook;
  }
  return false;
}

/** Would the attacker itself be capturable on the victim's square after taking? */
function victimIsProtected(pos: Position, move: Move, defender: Color): boolean {
  const undo = pos.makeMoveFast(move);
  const exposed = isSquareAttacked(pos, move.to, defender);
  pos.unmakeMove(undo);
  return exposed;
}

/**
 * The squares of the enemy pieces `by` is chasing in this position.
 *
 * `fromSquare` restricts the threats to those made by the piece standing there,
 * and the caller should pass the square the last move landed on. That is not an
 * optimisation, it is the rule: a side that shuffles one piece while a DIFFERENT
 * piece of its own has been sitting on a standing threat is not chasing anything,
 * because nothing it did renewed the threat. Without this, a game where Black
 * voluntarily bounced a cannon between two squares a red chariot happened to
 * cover was ruled a perpetual chase against Red, who had merely moved a rook back
 * and forth. That was a false positive found by reading a passing test's output
 * rather than trusting it.
 *
 * The cost is a false NEGATIVE: a chase created by discovery -- moving a blocker
 * so a piece behind it attacks -- is not seen. That is the right way round. Every
 * exception here exists to avoid taking a game off someone who should have drawn.
 *
 * Threats are taken from LEGAL moves, not pseudo-legal ones: a capture that
 * would expose your own general is not a threat you could carry out, so it does
 * not chase anything.
 *
 * The four exceptions, in the order the cost of getting them wrong suggests:
 *   - a general or a soldier never counts as the chaser;
 *   - a soldier that has not crossed the river is not worth chasing;
 *   - a same-type standoff (車 facing 車) is mutual, so neither side is chasing;
 *   - a protected victim is not chased, unless the exchange is good anyway.
 *
 * KNOWN LIMIT. Protection is evaluated in the position after the capture, which
 * is the straightforward reading. The manual's diagrams 39, 40, 41 and 61 turn on
 * protection-SUBVERSION, where a move removes the defender and the pre-move
 * reading differs -- those are the cases Pikafish got wrong in 2023, and they are
 * exactly what the 110-diagram casebook exists to settle. We have no
 * machine-readable casebook, so this implementation is not validated against
 * them. See CHASE_VALIDATED_AGAINST_CASEBOOK.
 */
export function chasedSquares(pos: Position, by: Color, fromSquare?: Square): Set<Square> {
  const out = new Set<Square>();
  const them = opponent(by);
  for (const move of legalMoves(pos, by)) {
    if (fromSquare !== undefined && move.from !== fromSquare) continue;
    const victim = pos.pieceAt(move.to);
    if (!victim || victim.color !== them) continue;
    const attacker = pos.pieceAt(move.from);
    if (!attacker) continue;
    if (attacker.type === PieceType.King || attacker.type === PieceType.Pawn) continue;
    if (victim.type === PieceType.Pawn && !pawnHasCrossed(move.to, them)) continue;
    if (attacker.type === victim.type && isSquareAttacked(pos, move.from, them)) continue;
    if (!exchangeAlwaysGood(attacker.type, victim.type) && victimIsProtected(pos, move, them)) continue;
    out.add(move.to);
  }
  return out;
}

/**
 * Which sides chased the SAME piece on every one of their own moves in the cycle.
 *
 * Intersection, not union: chasing a different piece each time is not a
 * perpetual chase, it is just an active position. A side with no moves in the
 * cycle is never the chaser.
 */
export function perpetualChasers(cycle: readonly CyclePly[]): { red: boolean; black: boolean } {
  const narrow = (side: Color): boolean => {
    let acc: Set<number> | null = null;
    for (const ply of cycle) {
      if (ply.mover !== side) continue;
      if (acc === null) acc = new Set(ply.chasedIds);
      else for (const id of [...acc]) if (!ply.chasedIds.includes(id)) acc.delete(id);
      if (acc.size === 0) return false;
    }
    return acc !== null && acc.size > 0;
  };
  return { red: narrow(Color.Red), black: narrow(Color.Black) };
}

/**
 * Adjudicate a repeated cycle: the violation lattice, in order.
 *
 * Idle (0) < Chase (1) < Check (2); the side at the strictly higher level loses,
 * equal levels draw. The order of the tests below is the law, not convenience:
 *
 *   1. Perpetual CHECK first, because it outranks everything.
 *   2. Then, if a check appeared ANYWHERE in the cycle without either side
 *      checking throughout, the cycle is a draw and chase is not considered at
 *      all. This is 一將一捉 -- alternating a check with a chase is permitted --
 *      and it is deliberately the conservative reading: it can only produce
 *      fewer chase rulings, never more.
 *   3. Only a cycle with no check in it can be a perpetual chase.
 */
export function adjudicateCycle(cycle: readonly CyclePly[]): GameResult {
  const checkers = perpetualCheckers(cycle);
  if (checkers.red || checkers.black) return adjudicatePerpetualCheck(checkers.red, checkers.black);
  if (cycle.some((ply) => ply.gaveCheck)) return GameResult.Draw;

  const chasers = perpetualChasers(cycle);
  if (chasers.red && chasers.black) return GameResult.Draw; // equal fault
  if (chasers.red) return GameResult.BlackWins; // Red is the sole chaser -> Red loses
  if (chasers.black) return GameResult.RedWins;
  return GameResult.Draw; // a genuinely idle repetition, which the law permits
}

/** The lattice level each side reached in this cycle, for callers that want to explain a ruling. */
export function violationLevels(cycle: readonly CyclePly[]): { red: ViolationLevel; black: ViolationLevel } {
  const checkers = perpetualCheckers(cycle);
  const anyCheck = cycle.some((ply) => ply.gaveCheck);
  const chasers = anyCheck ? { red: false, black: false } : perpetualChasers(cycle);
  const level = (check: boolean, chase: boolean): ViolationLevel =>
    check ? ViolationLevel.Check : chase ? ViolationLevel.Chase : ViolationLevel.Idle;
  return {
    red: level(checkers.red, chasers.red),
    black: level(checkers.black, chasers.black),
  };
}

/**
 * Perpetual-chase adjudication now exists (chasedSquares + perpetualChasers).
 */
export const CHASE_ADJUDICATION_IMPLEMENTED = true;

/**
 * ...but it has NOT been checked against the WXF 110-diagram casebook, because
 * there is no machine-readable copy of it: the manual is a scan. The
 * unambiguous cases are covered by tests; the protection-subversion diagrams
 * (39, 40, 41, 61) are not, and they are the ones published engines get wrong.
 *
 * Keep these two flags separate and keep the site's copy written from BOTH. The
 * single flag they replace is what let "implemented" get published as "verified".
 */
export const CHASE_VALIDATED_AGAINST_CASEBOOK = false;
